import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';

/**
 * Marché live BRVM — page d'administration.
 *
 * ⚠️ Les données servies ici sont ÉCHANTILLONNÉES toutes les 15 minutes sur la
 * page publique de la BRVM, pas issues du bulletin officiel. `plus_haut_observe`
 * et `plus_bas_observe` sont des MINORANTS de l'amplitude réelle de la séance :
 * un pic survenu entre deux relevés est invisible. Tout affichage construit sur
 * ce hook doit le dire — d'où `nb_observations`, qui est la mesure de confiance
 * associée à chaque ligne.
 */

export interface LiveRun {
    id: number;
    date_seance: string | null;
    releve_at: string | null;
    capture_at: string | null;
    statut: 'OK' | 'PERIME' | 'ERREUR';
    nb_lignes: number;
    duree_ms: number | null;
    message: string | null;
}

export interface LiveIndice {
    code: string;
    libelle: string;
    valeur: number | null;
    variation_pct: number | null;
}

export interface LiveActivite {
    valeur_transactions: number | null;
    capitalisation_actions: number | null;
    capitalisation_obligations: number | null;
    indices: LiveIndice[];
}

export interface LiveCours {
    societe_symbole: string;
    nom_affiche: string | null;
    releve_at: string | null;
    volume: number | null;
    cours_veille: number | null;
    cours_ouverture: number | null;
    /** Colonne « Cours Clôture » de la BRVM : en séance, c'est le DERNIER cours traité. */
    cours_dernier: number | null;
    variation_pct: number | null;
    a_traite: boolean;
}

export interface LiveOhlc {
    societe_symbole: string;
    ouverture: number | null;
    plus_haut_observe: number | null;
    plus_bas_observe: number | null;
    cloture_observee: number | null;
    volume_observe: number | null;
    nb_observations: number;
    nb_releves_seance: number;
    premiere_obs_at: string | null;
    derniere_obs_at: string | null;
    ecart_cloture_boc: number | null;
}

export type LivePhase =
    | 'AVANT_SEANCE'
    | 'PRE_OUVERTURE'
    | 'CONTINU'
    | 'PRE_CLOTURE'
    | 'DERNIER_COURS'
    | 'APRES_SEANCE'
    | 'WEEKEND'
    | 'FERIE';

/** État de la séance, calculé par l'API (horaires officiels BRVM, week-ends, jours fériés). */
export interface LiveSeance {
    heure_marche: string;
    jour_de_bourse: boolean;
    /** Le marché accepte des ordres (09:00 – 15:00 UTC). */
    ouvert: boolean;
    /** Des transactions peuvent s'exécuter : faux en pré-ouverture et en pré-clôture. */
    echanges_possibles: boolean;
    phase: LivePhase;
    libelle: string;
    /** Fin de la phase en cours, ou prochaine pré-ouverture si le marché est fermé. */
    changement_at: string | null;
    minutes_avant_changement: number | null;
}

export interface LiveMeta {
    age_minutes: number | null;
    /** Fait brut : âge du relevé au-delà du seuil. Une anomalie seulement si `publication_attendue`. */
    perime: boolean;
    publication_attendue: boolean;
    seance: LiveSeance;
    seuil_fraicheur_minutes: number;
    dernier_essai: LiveRun | null;
    nb_valeurs: number;
    nb_valeurs_traitees: number;
    source: string;
}

export interface LivePoint {
    releve_at: string | null;
    cours_dernier: number | null;
    volume: number | null;
    a_traite: boolean;
}

export interface LiveSerie {
    symbole: string;
    nom_affiche: string | null;
    points: LivePoint[];
    plus_haut_observe: number | null;
    plus_bas_observe: number | null;
    nb_points: number;
}

/** Cadence de rafraîchissement de l'écran, alignée sur AdminNiaDashboard. */
const INTERVALLE_MS = 60_000;

export function useLiveMarket() {
    const { token } = useAuth();
    const [run, setRun] = useState<LiveRun | null>(null);
    const [activite, setActivite] = useState<LiveActivite | null>(null);
    const [cours, setCours] = useState<LiveCours[]>([]);
    const [ohlc, setOhlc] = useState<Record<string, LiveOhlc>>({});
    const [runs, setRuns] = useState<LiveRun[]>([]);
    const [meta, setMeta] = useState<LiveMeta | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [captureEnCours, setCaptureEnCours] = useState(false);

    // Évite un setState après démontage quand une capture manuelle (jusqu'à
    // 90 s côté API) se termine alors que l'admin a quitté l'écran.
    const monte = useRef(true);
    useEffect(() => {
        monte.current = true;
        return () => {
            monte.current = false;
        };
    }, []);

    const entetes = useCallback(
        () => ({ Authorization: `Bearer ${token}` }),
        [token]
    );

    const charger = useCallback(async () => {
        if (!token) return;
        setError(null);
        try {
            const [rLatest, rOhlc, rRuns] = await Promise.all([
                fetch(`${API_URL}/v1/admin/live-market/latest`, { headers: entetes() }),
                fetch(`${API_URL}/v1/admin/live-market/ohlc`, { headers: entetes() }),
                fetch(`${API_URL}/v1/admin/live-market/runs`, { headers: entetes() }),
            ]);

            if (!rLatest.ok) {
                setError(
                    rLatest.status === 403
                        ? 'Accès refusé : permission admin_platform requise.'
                        : 'Erreur serveur lors de la récupération du marché live.'
                );
                return;
            }

            const latest = await rLatest.json();
            if (!monte.current) return;
            setRun(latest.data?.run ?? null);
            setActivite(latest.data?.activite ?? null);
            setCours(latest.data?.cours ?? []);
            setMeta(latest.meta ?? null);

            if (rOhlc.ok) {
                const corps = await rOhlc.json();
                const lignes: LiveOhlc[] = corps.data?.lignes ?? [];
                const parSymbole: Record<string, LiveOhlc> = {};
                lignes.forEach((l) => {
                    parSymbole[l.societe_symbole] = l;
                });
                if (monte.current) setOhlc(parSymbole);
            }

            if (rRuns.ok) {
                const corps = await rRuns.json();
                if (monte.current) setRuns(corps.data?.runs ?? []);
            }
        } catch (err) {
            console.error('Erreur marché live :', err);
            if (monte.current) setError('Erreur réseau. Vérifiez la connexion API.');
        } finally {
            if (monte.current) setLoading(false);
        }
    }, [token, entetes]);

    /**
     * Déclenche une capture immédiate.
     *
     * `forcer` n'est PAS exposé : sans déduplication, une page BRVM figée
     * produirait des observations en double et gonflerait `nb_observations`,
     * c'est-à-dire la fiabilité affichée des extrêmes. Un statut PERIME en
     * retour est une réponse normale, pas un échec.
     */
    const capturer = useCallback(async (): Promise<string | null> => {
        if (!token) return null;
        setCaptureEnCours(true);
        try {
            const reponse = await fetch(`${API_URL}/v1/admin/live-market/snapshot`, {
                method: 'POST',
                headers: entetes(),
            });
            if (!reponse.ok) {
                setError('La capture a échoué côté serveur.');
                return null;
            }
            const corps = await reponse.json();
            await charger();
            return (corps.data?.statut as string) ?? null;
        } catch (err) {
            console.error('Erreur capture marché live :', err);
            setError('Erreur réseau pendant la capture.');
            return null;
        } finally {
            if (monte.current) setCaptureEnCours(false);
        }
    }, [token, entetes, charger]);

    const chargerSerie = useCallback(
        async (symbole: string): Promise<LiveSerie | null> => {
            if (!token) return null;
            try {
                const reponse = await fetch(
                    `${API_URL}/v1/admin/live-market/intraday/${encodeURIComponent(symbole)}`,
                    { headers: entetes() }
                );
                if (!reponse.ok) return null;
                const corps = await reponse.json();
                return {
                    symbole: corps.data?.symbole ?? symbole,
                    nom_affiche: corps.data?.nom_affiche ?? null,
                    points: corps.data?.points ?? [],
                    plus_haut_observe: corps.meta?.plus_haut_observe ?? null,
                    plus_bas_observe: corps.meta?.plus_bas_observe ?? null,
                    nb_points: corps.meta?.nb_points ?? 0,
                };
            } catch (err) {
                console.error('Erreur série intraday :', err);
                return null;
            }
        },
        [token, entetes]
    );

    useEffect(() => {
        if (!token) {
            setLoading(false);
            return;
        }
        charger();
        const minuteur = setInterval(charger, INTERVALLE_MS);
        return () => clearInterval(minuteur);
    }, [token, charger]);

    return {
        run,
        activite,
        cours,
        ohlc,
        runs,
        meta,
        loading,
        error,
        captureEnCours,
        capturer,
        chargerSerie,
        refetch: charger,
    };
}
